import React, { useContext, useEffect } from 'react';
import style from './index.module.css';
import { connectPagination } from 'react-instantsearch-dom';
import { min, max, range } from 'lodash';
import { Label } from '../../../atoms';
import Dots from '../../../static/images/more_horizontal.svg';
import ChevronUp from '../../../static/images/chevron_up.svg';
import { HitsContext } from '../../../app';

type PaginationComponentProps = {
    currentRefinement: number;
    nbPages: number;
    refine: any;
    createURL: any;
};

type Props = {
    page?: number;
    currentRefinement: number;
    nbPages?: number;
    refine: any;
    createURL: any;
};

const renderPrevPageLink = ({ currentRefinement, refine, createURL }: Props) => {
    const hitsContext = useContext(HitsContext);
    const page = max([currentRefinement - 1, 1]);
    const LinkBody = () => (
        <>
            <span className={`${style.vector} ${style.left}`}>
                <ChevronUp />
            </span>
            <span className={style.text}>
                <Label semibold>Previous</Label>
            </span>
        </>
    );

    return (
        <li key="prev" className={style.itemWrapper}>
            {currentRefinement !== page ? (
                <a
                    href={createURL(page)}
                    className={style.item}
                    onClick={(event) => {
                        event.preventDefault();
                        refine(page);
                        window.scroll({
                            top: hitsContext.topPosition,
                            left: 0,
                            behavior: 'smooth',
                        });
                    }}
                >
                    <LinkBody />
                </a>
            ) : (
                <div className={`${style.item} ${style.disabled}`}>
                    <LinkBody />
                </div>
            )}
        </li>
    );
};

const renderNextPageLink = ({ currentRefinement, refine, createURL, nbPages }: Props) => {
    const hitsContext = useContext(HitsContext);
    const page = min([currentRefinement + 1, nbPages]);
    const LinkBody = () => (
        <>
            <span className={style.text}>
                <Label semibold>Next</Label>
            </span>
            <span className={`${style.vector} ${style.right}`}>
                <ChevronUp />
            </span>
        </>
    );

    return (
        <li key="next" className={style.itemWrapper}>
            {currentRefinement !== page ? (
                <a
                    href={createURL(page)}
                    className={style.item}
                    onClick={(event) => {
                        event.preventDefault();
                        refine(page);
                        window.scroll({
                            top: hitsContext.topPosition,
                            left: 0,
                            behavior: 'smooth',
                        });
                    }}
                >
                    <LinkBody />
                </a>
            ) : (
                <div className={`${style.item} ${style.disabled}`}>
                    <LinkBody />
                </div>
            )}
        </li>
    );
};

const renderPageLink = ({ page, currentRefinement, refine, createURL }: Props) => {
    const hitsContext = useContext(HitsContext);

    return (
        <li key={page} className={style.itemWrapper}>
            <a
                className={`${style.item} ${style.pageLink} ${currentRefinement === page ? style.current : ''}`}
                href={createURL(page)}
                onClick={(event) => {
                    event.preventDefault();
                    refine(page);
                    window.scroll({
                        top: hitsContext.topPosition,
                        left: 0,
                        behavior: 'smooth',
                    });
                }}
            >
                <Label semibold>{page}</Label>
            </a>
        </li>
    );
};

const renderDots = () => (
    <li className={style.itemWrapper}>
        <Dots />
    </li>
);

const PaginationComponent = ({
    currentRefinement,
    nbPages,
    refine,
    createURL,
    tab,
}: PaginationComponentProps & { tab: string }) => {
    const startPos = min([max([currentRefinement, 3]), nbPages - 2]); //we use startPos if nbPages > 5, so startPos always be positive
    const panel = nbPages <= 5 ? range(1, nbPages + 1) : range(startPos - 1, startPos + 2);

    useEffect(() => {
        refine(1);
    }, [tab]);

    return (
        <ul className={style.pagination}>
            {nbPages > 1 &&
                renderPrevPageLink({
                    currentRefinement,
                    refine,
                    createURL,
                })}
            {nbPages > 5 &&
                renderPageLink({
                    page: 1,
                    currentRefinement,
                    refine,
                    createURL,
                })}
            {nbPages > 5 && currentRefinement > 3 && renderDots()}
            {panel.map((page) => renderPageLink({ page, currentRefinement, refine, createURL }))}
            {nbPages > 5 && currentRefinement < nbPages - 2 && renderDots()}
            {nbPages > 5 &&
                renderPageLink({
                    page: nbPages,
                    currentRefinement,
                    refine,
                    createURL,
                })}
            {nbPages > 1 &&
                renderNextPageLink({
                    currentRefinement,
                    refine,
                    createURL,
                    nbPages,
                })}
        </ul>
    );
};

export const Pagination = connectPagination(PaginationComponent);
